import { BarChart3, RotateCw } from "lucide-react";
import type { JSX, ReactNode } from "react";
import { useId, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { PAGE_COLUMN } from "@/components/ui/redesign/page";
import { Skeleton } from "@/components/ui/skeleton";
import { V3Button } from "@/components/ui/v3/button";
import { V3Chip } from "@/components/ui/v3/chip";
import { type ActivityDay, axisTicks, visitsPerDay } from "@/lib/activity";
import { type ActivityRange, useOwnActivity } from "@/lib/activity-queries";
import { cn } from "@/lib/utils";

const RANGES: ActivityRange[] = [7, 30, 90];

const Tile = ({
  label,
  value,
  hint,
  testId,
}: {
  label: string;
  value: ReactNode;
  hint?: ReactNode;
  testId: string;
}): JSX.Element => (
  <div
    className="rounded-v3-panel bg-v3-panel px-4 py-3.5 shadow-v3-hairline-strong sm:px-[18px] sm:py-4"
    data-testid={testId}
  >
    <p className="text-[12px] font-medium uppercase tracking-[0.04em] text-v3-text-3">
      {label}
    </p>
    <p className="mt-1.5 font-v3-display text-[26px] font-bold leading-8 tracking-[-0.02em] tabular-nums text-v3-text sm:text-[28px]">
      {value}
    </p>
    {hint && (
      <p className="mt-0.5 text-[12.5px] leading-[18px] text-v3-text-3">
        {hint}
      </p>
    )}
  </div>
);

/**
 * Visits per day: one series in the v3.2 brand (no legend; the title names
 * it), bars anchored at the baseline with 4px rounded tops and 2px gaps, a
 * recessive grid, a tooltip on hover or arrow keys, and the same numbers as
 * a table for screen readers.
 */
export const VisitsPerDayChart = ({
  days,
  locale,
}: {
  days: ActivityDay[];
  locale: string;
}): JSX.Element => {
  const { t } = useTranslation();
  const titleId = useId();
  const [active, setActive] = useState<number | null>(null);
  const ticks = axisTicks(Math.max(0, ...days.map((day) => day.visits)));
  const top = ticks.at(-1) ?? 4;
  const dayLabel = (day: ActivityDay, long = false): string =>
    day.date.toLocaleDateString(
      locale,
      long
        ? { weekday: "short", month: "short", day: "numeric" }
        : { month: "short", day: "numeric" },
    );
  // Up to about six date labels under the bars.
  const labelEvery = Math.max(1, Math.ceil(days.length / 6));
  const current = active === null ? null : days[active];
  const describe = (day: ActivityDay): string =>
    t("analyticsV32.chart.tooltip", {
      date: dayLabel(day, true),
      count: day.visits,
    });
  return (
    <figure className="m-0">
      <figcaption id={titleId} className="sr-only">
        {t("analyticsV32.chart.title")}
      </figcaption>
      <div className="relative">
        <div
          aria-describedby={`${titleId}-live`}
          aria-labelledby={titleId}
          className="relative h-[200px] rounded-lg outline-none focus-visible:ring-2 focus-visible:ring-v3-brand-ring sm:h-[220px]"
          // One tab stop that moves through the days (left/right), each read
          // out; the table below has the same numbers.
          aria-valuemax={days.length}
          aria-valuemin={1}
          aria-valuenow={(active ?? days.length - 1) + 1}
          aria-valuetext={describe(
            days[active ?? days.length - 1] ?? (days[0] as ActivityDay),
          )}
          data-testid="analytics-chart"
          role="slider"
          tabIndex={0}
          onBlur={() => setActive(null)}
          onKeyDown={(event) => {
            if (event.key !== "ArrowLeft" && event.key !== "ArrowRight") return;
            event.preventDefault();
            const step = event.key === "ArrowLeft" ? -1 : 1;
            setActive((previous) =>
              Math.min(
                days.length - 1,
                Math.max(0, (previous ?? days.length - 1) + step),
              ),
            );
          }}
          onMouseLeave={() => setActive(null)}
        >
          {/* Recessive grid and y values. */}
          <div aria-hidden className="absolute inset-0 bottom-6 left-7">
            {ticks.map((tick) => (
              <div
                key={tick}
                className="absolute inset-x-0 border-t border-v3-border"
                style={{ bottom: `${(tick / top) * 100}%` }}
              >
                <span className="absolute -left-7 -top-2 w-5 text-right text-[11px] tabular-nums text-v3-text-3">
                  {tick}
                </span>
              </div>
            ))}
          </div>
          <div
            aria-hidden
            className="absolute inset-0 bottom-6 left-7 flex items-end gap-[2px]"
          >
            {days.map((day, index) => (
              // biome-ignore lint/a11y/noStaticElementInteractions: hover only shows the tooltip; keyboard users use the arrow keys on the chart.
              <div
                key={day.key}
                className="relative flex h-full min-w-0 flex-1 items-end"
                onMouseEnter={() => setActive(index)}
              >
                <div
                  className={cn(
                    // Ink, as the rest of v3.2 (no brand colour for data);
                    // the bar under the pointer or focus a little stronger.
                    "w-full rounded-t-[4px] bg-v3-ink opacity-70 transition-opacity",
                    active === index && "opacity-100",
                  )}
                  data-testid="analytics-bar"
                  style={{
                    height: `${(day.visits / top) * 100}%`,
                    minHeight: day.visits > 0 ? 2 : 0,
                  }}
                />
              </div>
            ))}
          </div>
          {/* Date labels. */}
          <div
            aria-hidden
            className="absolute inset-x-0 bottom-0 left-7 flex h-5 gap-[2px]"
          >
            {days.map((day, index) => (
              <span
                key={day.key}
                className="relative min-w-0 flex-1 text-[11px] text-v3-text-3"
              >
                {index % labelEvery === 0 && (
                  <span className="absolute left-0 top-0.5 whitespace-nowrap">
                    {dayLabel(day)}
                  </span>
                )}
              </span>
            ))}
          </div>
          {current && active !== null && (
            <div
              aria-hidden
              className="pointer-events-none absolute -top-2 z-10 -translate-x-1/2 -translate-y-full whitespace-nowrap rounded-lg bg-v3-panel px-2.5 py-1.5 text-[12.5px] font-medium text-v3-text shadow-v3-pop"
              data-testid="analytics-tooltip"
              style={{
                left: `calc(1.75rem + (100% - 1.75rem) * ${(active + 0.5) / days.length})`,
              }}
            >
              {describe(current)}
            </div>
          )}
        </div>
        <p aria-live="polite" className="sr-only" id={`${titleId}-live`}>
          {current ? describe(current) : t("analyticsV32.chart.keys")}
        </p>
      </div>
      <table className="sr-only" data-testid="analytics-table">
        <caption>{t("analyticsV32.chart.title")}</caption>
        <thead>
          <tr>
            <th scope="col">{t("analyticsV32.chart.day")}</th>
            <th scope="col">{t("analyticsV32.chart.visits")}</th>
          </tr>
        </thead>
        <tbody>
          {days.map((day) => (
            <tr key={day.key}>
              <th scope="row">{dayLabel(day, true)}</th>
              <td>{day.visits}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </figure>
  );
};

/**
 * Analytics in the v3.2 app (VITE_APP_REDESIGN_ENABLED): visits, recorded
 * time and note-ready time for the last 7, 30 or 90 days, and visits per
 * day. A tile shows only when there is data for it (notes copied is not
 * recorded anywhere, so it is not shown).
 */
export const AnalyticsMonth = (): JSX.Element => {
  const { t, i18n } = useTranslation();
  const locale = i18n.language || "en";
  const [days, setDays] = useState<ActivityRange>(30);
  const { data, loading, error, refresh } = useOwnActivity(days);
  const perDay = useMemo(
    () => (data ? visitsPerDay(data.recordings, days) : []),
    [data, days],
  );
  const number = (value: number, digits = 0): string =>
    value.toLocaleString(locale, { maximumFractionDigits: digits });
  const hours = data ? data.recordedSeconds / 3600 : 0;
  const perVisitMinutes =
    data && data.visits > 0 ? data.recordedSeconds / 60 / data.visits : 0;

  return (
    <div
      className={cn(PAGE_COLUMN, "font-v3 text-v3-text")}
      data-testid="analytics-v32"
    >
      <h1 className="font-v3-display text-[26px] font-bold leading-8 tracking-[-0.02em] sm:text-[30px] sm:leading-9">
        {t("analyticsV32.range.heading", { count: days })}
      </h1>
      <p className="mt-1 text-[14px] text-v3-text-2">
        {t("analyticsV32.subtitle")}
      </p>
      <fieldset className="mt-4 flex flex-wrap gap-2 border-0">
        <legend className="sr-only">{t("analyticsV32.range.label")}</legend>
        {RANGES.map((range) => (
          <V3Chip
            key={range}
            aria-pressed={days === range}
            data-testid={`analytics-range-${range}`}
            onClick={() => setDays(range)}
          >
            {t("analyticsV32.range.days", { count: range })}
          </V3Chip>
        ))}
      </fieldset>

      {loading ? (
        <div
          aria-busy
          aria-label={t("common.loading")}
          className="mt-5 space-y-3"
          data-testid="analytics-loading"
          role="status"
        >
          <div className="grid grid-cols-2 gap-3 lg:grid-cols-3">
            {[0, 1, 2].map((index) => (
              <Skeleton
                key={index}
                className="h-[98px] rounded-v3-panel bg-v3-surface-2"
              />
            ))}
          </div>
          <Skeleton className="h-[280px] rounded-v3-panel bg-v3-surface-2" />
        </div>
      ) : error || !data ? (
        <div
          className="mt-5 flex flex-col items-start gap-3 rounded-v3-panel bg-v3-rec-tint p-4 sm:flex-row sm:items-center"
          role="alert"
        >
          <p className="flex-1 text-[14px] text-v3-text">
            {t("analyticsV32.error")}
          </p>
          <V3Button
            onClick={() => {
              void refresh();
            }}
          >
            <RotateCw />
            {t("common.retry")}
          </V3Button>
        </div>
      ) : (
        <>
          <div
            className="mt-5 grid grid-cols-2 gap-3 lg:grid-cols-3"
            data-testid="analytics-tiles"
          >
            <Tile
              label={t("analyticsV32.tiles.visits")}
              testId="analytics-tile-visits"
              value={number(data.visits)}
            />
            {data.recordedSeconds > 0 && (
              <Tile
                hint={t("analyticsV32.tiles.perVisit", {
                  minutes: number(perVisitMinutes),
                })}
                label={t("analyticsV32.tiles.recorded")}
                testId="analytics-tile-recorded"
                value={t("analyticsV32.tiles.hours", {
                  hours: number(hours, hours < 10 ? 1 : 0),
                })}
              />
            )}
            {data.noteReady.medianSeconds !== null &&
              data.noteReady.count > 0 && (
                <Tile
                  hint={t("analyticsV32.tiles.noteReadyHint", {
                    count: data.noteReady.count,
                  })}
                  label={t("analyticsV32.tiles.noteReady")}
                  testId="analytics-tile-note-ready"
                  value={
                    data.noteReady.medianSeconds < 120
                      ? t("analyticsV32.tiles.seconds", {
                          seconds: number(data.noteReady.medianSeconds),
                        })
                      : t("analyticsV32.tiles.minutes", {
                          minutes: number(data.noteReady.medianSeconds / 60, 1),
                        })
                  }
                />
              )}
          </div>
          <section
            aria-labelledby="analytics-chart-title"
            className="mt-3 rounded-v3-panel bg-v3-panel px-4 pb-4 pt-4 shadow-v3-hairline-strong sm:px-5"
          >
            <h2
              className="text-[15px] font-semibold"
              id="analytics-chart-title"
            >
              {t("analyticsV32.chart.title")}
            </h2>
            <p className="text-[12.5px] text-v3-text-3">
              {t("analyticsV32.range.heading", { count: days })}
            </p>
            {data.visits === 0 ? (
              <div
                className="flex flex-col items-center px-6 py-10 text-center"
                data-testid="analytics-empty"
              >
                <span className="flex size-11 items-center justify-center rounded-full bg-v3-surface-2 text-v3-text-2 shadow-v3-hairline">
                  <BarChart3 aria-hidden className="size-5" />
                </span>
                <p className="mt-3 text-[15px] font-semibold">
                  {t("analyticsV32.empty.title")}
                </p>
                <p className="mt-1 max-w-[320px] text-[13.5px] text-v3-text-3">
                  {t("analyticsV32.empty.hint")}
                </p>
              </div>
            ) : (
              <div className="mt-4 pt-6">
                <VisitsPerDayChart days={perDay} locale={locale} />
              </div>
            )}
          </section>
        </>
      )}
    </div>
  );
};
